/**
 * Página de Gestión Financiera
 * Cobros, Pagos y Gastos del Club
 */

import { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Select,
  InlineLoader,
} from "@/components/ui";
import {
  DollarSign,
  CreditCard,
  TrendingDown,
  TrendingUp,
  Receipt,
  Search,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  FileText,
  Wallet,
} from "lucide-react";
import {
  useFinancialSummary,
  useChargesByClubAndPeriod,
  usePaymentsByClubAndPeriod,
  useExpensesByClubAndPeriod,
} from "@/hooks/useFinance";
import { useAcademicPeriods } from "@/hooks/useCatalogs";
import { useMembersByClub } from "@/hooks/usePersons";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import { Can } from "@/components/auth/Can";
import ChargeModal from "./components/ChargeModal";
import BulkChargeModal from "./components/BulkChargeModal";
import MemberStatementModal from "./components/MemberStatementModal";
import { PendingReceivablesModal } from "./components/PendingReceivablesModal";
import PaymentModal from "./components/PaymentModal";
import ExpenseModal from "./components/ExpenseModal";
import type { Charge, Member } from "@/types";

type TabType = "overview" | "charges" | "payments" | "expenses";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  partial: "bg-orange-100 text-orange-800",
  paid: "bg-green-100 text-green-800",
  overdue: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
  valid: "bg-green-100 text-green-800",
  reversed: "bg-red-100 text-red-800",
};

const statusLabels: Record<string, string> = {
  pending: "Pendiente",
  partial: "Pago Parcial",
  paid: "Pagado",
  overdue: "Vencido",
  cancelled: "Cancelado",
  valid: "Válido",
  reversed: "Reversado",
};

const paymentMethodLabels: Record<string, string> = {
  efectivo: "💵 Efectivo",
  transferencia: "🏦 Transferencia",
  nequi: "📱 Nequi",
  daviplata: "📱 Daviplata",
  otro: "📝 Otro",
};

export default function FinancePage() {
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [selectedClubId, setSelectedClubId] = useState<string>("");
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  // Modals
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showBulkChargeModal, setShowBulkChargeModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [showReceivablesModal, setShowReceivablesModal] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState<Charge | null>(null);
  const [selectedMemberForStatement, setSelectedMemberForStatement] =
    useState<Member | null>(null);

  // Data - usando scope del usuario
  const { profile } = useAuthStore();
  const { isClubDirector } = usePermissions();
  const { data: clubs, isLoading: loadingClubs } = useAccessibleClubs();
  const { data: periods } = useAcademicPeriods();
  const activePeriod = periods?.find((p) => p.status === "active");

  // Auto-select club based on user role and active period
  // Club directors use their club, admins can select
  useEffect(() => {
    if (!selectedClubId) {
      if (isClubDirector && profile?.directorOfClub) {
        setSelectedClubId(profile.directorOfClub.id);
      } else if (clubs?.[0]) {
        setSelectedClubId(clubs[0].id);
      }
    }
  }, [selectedClubId, isClubDirector, profile?.directorOfClub, clubs]);

  useEffect(() => {
    if (!selectedPeriodId && activePeriod) {
      setSelectedPeriodId(activePeriod.id);
    }
  }, [selectedPeriodId, activePeriod]);

  const { data: summary, isLoading: loadingSummary } = useFinancialSummary(
    selectedClubId,
    selectedPeriodId,
  );
  const { data: charges, isLoading: loadingCharges } =
    useChargesByClubAndPeriod(selectedClubId, selectedPeriodId);
  const { data: payments, isLoading: loadingPayments } =
    usePaymentsByClubAndPeriod(selectedClubId, selectedPeriodId);
  const { data: expenses, isLoading: loadingExpenses } =
    useExpensesByClubAndPeriod(selectedClubId, selectedPeriodId);
  const { data: members } = useMembersByClub(selectedClubId);

  // Get selected club name
  const selectedClubName = useMemo(() => {
    return clubs?.find((c) => c.id === selectedClubId)?.name || "Club";
  }, [clubs, selectedClubId]);

  // Filtered data
  const filteredCharges = useMemo(() => {
    if (!charges) return [];
    if (!searchTerm) return charges;
    return charges.filter(
      (c) =>
        c.personName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.conceptName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [charges, searchTerm]);

  const filteredPayments = useMemo(() => {
    if (!payments) return [];
    if (!searchTerm) return payments;
    return payments.filter((p) =>
      p.personName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [payments, searchTerm]);

  const filteredExpenses = useMemo(() => {
    if (!expenses) return [];
    if (!searchTerm) return expenses;
    return expenses.filter(
      (e) =>
        e.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.category.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [expenses, searchTerm]);

  // Pending charges for quick action
  const pendingCharges =
    charges?.filter(
      (c) =>
        c.status === "pending" ||
        c.status === "partial" ||
        c.status === "overdue",
    ) || [];

  const handlePayCharge = (charge: Charge) => {
    setSelectedCharge(charge);
    setShowPaymentModal(true);
  };

  const handleViewStatement = (memberId: string) => {
    const member = members?.find((m) => m.id === memberId);
    if (member) {
      setSelectedMemberForStatement(member);
      setShowStatementModal(true);
    }
  };

  const formatDate = (timestamp: unknown) => {
    if (!timestamp) return "-";
    const ts = timestamp as { toDate?: () => Date };
    const date = ts.toDate ? ts.toDate() : new Date(timestamp as string);
    return date.toLocaleDateString("es-CO");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loadingClubs) {
    return (
      <div className="flex items-center justify-center h-64">
        <InlineLoader size="lg" label="Cargando finanzas…" />
      </div>
    );
  }

  if (!clubs?.length) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <DollarSign className="w-8 h-8 text-gray-500" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No hay clubes
        </h3>
        <p className="text-gray-500">
          Primero debes crear un club para gestionar finanzas
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30 shrink-0">
            <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Finanzas
            </h1>
            <p className="text-gray-500 text-sm sm:text-base">
              Gestiona cobros, pagos y gastos del club
            </p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={() => setShowReceivablesModal(true)}
            aria-label="Cartera Pendiente"
          >
            <Wallet className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Cartera Pendiente</span>
          </Button>
          <Can permission="finance.write" clubId={selectedClubId}>
            <Button
              variant="secondary"
              onClick={() => setShowChargeModal(true)}
              aria-label="Nuevo Cobro"
            >
              <Receipt className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nuevo Cobro</span>
            </Button>
          </Can>
          <Can permission="finance.write" clubId={selectedClubId}>
            <Button
              variant="secondary"
              onClick={() => setShowBulkChargeModal(true)}
              aria-label="Cobro Masivo"
            >
              <Users className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Cobro Masivo</span>
            </Button>
          </Can>
          <Can permission="finance.approve" clubId={selectedClubId}>
            <Button
              variant="secondary"
              onClick={() => setShowExpenseModal(true)}
              className="border-red-200 text-red-700 hover:bg-red-50 transition-colors duration-200"
              aria-label="Nuevo Gasto"
            >
              <TrendingDown className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nuevo Gasto</span>
            </Button>
          </Can>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
        <div className="w-full sm:w-64">
          <Select
            label="Ver finanzas del club"
            value={selectedClubId}
            onChange={(e) => setSelectedClubId(e.target.value)}
          >
            {clubs?.map((club) => (
              <option key={club.id} value={club.id}>
                {club.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-full sm:w-48">
          <Select
            label="Periodo"
            value={selectedPeriodId}
            onChange={(e) => setSelectedPeriodId(e.target.value)}
          >
            {periods?.map((period) => (
              <option key={period.id} value={period.id}>
                {period.name} {period.status === "active" && "(Actual)"}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      {loadingSummary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="animate-pulse">
                <div className="h-16 bg-gray-200 rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Ingresos</p>
                  <p className="text-2xl sm:text-3xl font-bold text-green-600 mt-1">
                    {formatCurrency(summary?.totalPaid || 0)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {summary?.paymentCount || 0} pagos
                  </p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Gastos</p>
                  <p className="text-2xl sm:text-3xl font-bold text-red-600 mt-1">
                    {formatCurrency(summary?.totalExpenses || 0)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {summary?.expenseCount || 0} gastos
                  </p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-red-400 to-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/30 group-hover:scale-110 transition-transform duration-300">
                  <TrendingDown className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Por Cobrar
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold text-orange-600 mt-1">
                    {formatCurrency(summary?.totalPending || 0)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {pendingCharges.length} pendientes
                  </p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Clock className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Balance Neto
                  </p>
                  <p
                    className={`text-2xl sm:text-3xl font-bold mt-1 ${
                      (summary?.netBalance || 0) >= 0
                        ? "text-blue-600"
                        : "text-red-600"
                    }`}
                  >
                    {formatCurrency(summary?.netBalance || 0)}
                  </p>
                  <p className="text-xs text-gray-500">Ingresos - Gastos</p>
                </div>
                <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                  <DollarSign className="w-7 h-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-gray-100 rounded-xl p-1.5 inline-flex gap-1 max-w-full overflow-x-auto">
        {[
          { key: "overview", label: "Resumen", icon: DollarSign },
          {
            key: "charges",
            label: "Cobros",
            icon: Receipt,
            count: charges?.length,
          },
          {
            key: "payments",
            label: "Pagos",
            icon: CreditCard,
            count: payments?.length,
          },
          {
            key: "expenses",
            label: "Gastos",
            icon: TrendingDown,
            count: expenses?.length,
          },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as TabType)}
            className={`flex items-center gap-1.5 sm:gap-2 py-1.5 sm:py-2 px-2.5 sm:px-4 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap ${
              activeTab === tab.key
                ? "bg-white text-primary-600 shadow-md"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.key
                    ? "bg-primary-100 text-primary-700"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      {activeTab !== "overview" && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setSearchTerm("")}
            placeholder="Buscar..."
            className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
          />
        </div>
      )}

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Charges */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                Cobros Pendientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingCharges.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <CheckCircle className="w-7 h-7 text-green-500" />
                  </div>
                  <p>No hay cobros pendientes</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingCharges.slice(0, 5).map((charge) => (
                    <div
                      key={charge.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {charge.personName}
                        </p>
                        <p className="text-sm font-medium text-gray-500">
                          {charge.conceptName}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">
                          {formatCurrency(charge.balance)}
                        </p>
                        <Button
                          size="sm"
                          onClick={() => handlePayCharge(charge)}
                          className="mt-1"
                        >
                          Pagar
                        </Button>
                      </div>
                    </div>
                  ))}
                  {pendingCharges.length > 5 && (
                    <button
                      onClick={() => setActiveTab("charges")}
                      className="w-full text-center text-sm text-primary-600 hover:text-primary-800 py-2 transition-colors duration-200"
                    >
                      Ver todos ({pendingCharges.length})
                    </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Payments */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-500" />
                Últimos Pagos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!payments?.length ? (
                <div className="text-center py-6 text-gray-500">
                  <div className="w-14 h-14 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <CreditCard className="w-7 h-7 text-gray-500" />
                  </div>
                  <p>No hay pagos registrados</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments
                    .filter((p) => p.status === "valid")
                    .slice(0, 5)
                    .map((payment) => (
                      <div
                        key={payment.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                      >
                        <div>
                          <p className="font-medium text-gray-900">
                            {payment.personName}
                          </p>
                          <p className="text-sm font-medium text-gray-500">
                            {paymentMethodLabels[payment.paymentMethod]} ·{" "}
                            {formatDate(payment.paymentDate)}
                          </p>
                        </div>
                        <p className="font-bold text-green-600">
                          +{formatCurrency(payment.amount)}
                        </p>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === "charges" && (
        <Card className="border-0 shadow-lg">
          <CardContent>
            {loadingCharges ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : !filteredCharges.length ? (
              <div className="text-center py-8 text-gray-500">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Receipt className="w-8 h-8 text-gray-500" />
                </div>
                <p>No hay cobros registrados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                        Persona
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                        Concepto
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                        Total
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                        Pagado
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                        Pendiente
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-500">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredCharges.map((charge) => (
                      <tr
                        key={charge.id}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {charge.personName}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {charge.conceptName}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">
                          {formatCurrency(charge.amount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-green-600">
                          {formatCurrency(charge.amountPaid)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-orange-600 font-medium">
                          {formatCurrency(charge.balance)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={statusColors[charge.status]}>
                            {statusLabels[charge.status]}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right space-x-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewStatement(charge.memberId)}
                            title="Ver Estado de Cuenta"
                          >
                            <FileText className="w-3 h-3" />
                          </Button>
                          {charge.status !== "paid" &&
                            charge.status !== "cancelled" && (
                              <Button
                                size="sm"
                                onClick={() => handlePayCharge(charge)}
                              >
                                <CreditCard className="w-3 h-3 mr-1" />
                                Pagar
                              </Button>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "payments" && (
        <Card className="border-0 shadow-lg">
          <CardContent>
            {loadingPayments ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : !filteredPayments.length ? (
              <div className="text-center py-8 text-gray-500">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CreditCard className="w-8 h-8 text-gray-500" />
                </div>
                <p>No hay pagos registrados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                        Persona
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                        Método
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                        Referencia
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                        Monto
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-500">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredPayments.map((payment) => (
                      <tr
                        key={payment.id}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(payment.paymentDate)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {payment.personName}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {paymentMethodLabels[payment.paymentMethod]}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {payment.reference || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-green-600">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={statusColors[payment.status]}>
                            {statusLabels[payment.status]}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "expenses" && (
        <Card className="border-0 shadow-lg">
          <CardContent>
            {loadingExpenses ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : !filteredExpenses.length ? (
              <div className="text-center py-8 text-gray-500">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <TrendingDown className="w-8 h-8 text-gray-500" />
                </div>
                <p>No hay gastos registrados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                        Categoría
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                        Descripción
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                        Proveedor
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                        Monto
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-500">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredExpenses.map((expense) => (
                      <tr
                        key={expense.id}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(expense.expenseDate)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                          {expense.category}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {expense.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {expense.vendor || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-red-600">
                          -{formatCurrency(expense.amount)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={statusColors[expense.status]}>
                            {statusLabels[expense.status]}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      {selectedClubId && selectedPeriodId && (
        <>
          <ChargeModal
            isOpen={showChargeModal}
            onClose={() => setShowChargeModal(false)}
            clubId={selectedClubId}
            periodId={selectedPeriodId}
          />

          <BulkChargeModal
            isOpen={showBulkChargeModal}
            onClose={() => setShowBulkChargeModal(false)}
            clubId={selectedClubId}
            periodId={selectedPeriodId}
          />

          {selectedCharge && (
            <PaymentModal
              isOpen={showPaymentModal}
              onClose={() => {
                setShowPaymentModal(false);
                setSelectedCharge(null);
              }}
              charge={selectedCharge}
            />
          )}

          <ExpenseModal
            isOpen={showExpenseModal}
            onClose={() => setShowExpenseModal(false)}
            clubId={selectedClubId}
            periodId={selectedPeriodId}
          />

          {selectedMemberForStatement && (
            <MemberStatementModal
              isOpen={showStatementModal}
              onClose={() => {
                setShowStatementModal(false);
                setSelectedMemberForStatement(null);
              }}
              member={selectedMemberForStatement}
            />
          )}

          <PendingReceivablesModal
            isOpen={showReceivablesModal}
            onClose={() => setShowReceivablesModal(false)}
            clubId={selectedClubId}
            clubName={selectedClubName}
          />
        </>
      )}
    </div>
  );
}
